import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { formatCurrency } from "../../utils/CurrencyUtils";
import { getWithToken, postWithToken } from "../../utils/Api";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";

export default function PriceScreen() {
    const [products, setProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState("");
    const [isEditing, setIsEditing] = useState({}); // State untuk mengontrol edit mode
    const [newPrice, setNewPrice] = useState({}); // State untuk menyimpan harga baru per produk
    const [subbrand, setSubbrand] = useState({data:[]});
    const [selectedSubbrand, setSelectedSubbrand] = useState("")

    useEffect(() => {
        fetchData(currentPage, search, selectedSubbrand);
        axios.get("https://api.millipoint.id/api/v2/subbrands")
            .then(res => {
                setSubbrand(res.data)
            })
    }, [currentPage, selectedSubbrand]);

    useEffect(() => {
        products.forEach(item => {
            setIsEditing((prev) => ({ ...prev, [item.id]: true })); // Masuk ke mode edit
            setNewPrice((prev) => ({ ...prev, [item.id]: item.active_price })); // Set nilai awal input harga baru
        })
    },[products])

    function fetchData(page, search, subbrand_id) {
        getWithToken(`/products?page=${page}&search=${search}&subbrand_id=${subbrand_id}`)
            .then((res) => {
                setProducts(res.data);
                setTotalPages(res.pagination.total_page);
            })
            .catch((e) => {
                console.error("Failed to fetch products", e);
                toast.error("Gagal mengambil data produk");
            });
    }

    function savePrice(id, old_active_price, new_active_price) {
        postWithToken(`/change-price`, {
            id,
            old_active_price,
            new_active_price,
        })
            .then((res) => {
                toast.success("Harga berhasil diperbarui");
                fetchData(currentPage, search, selectedSubbrand);
                setIsEditing((prev) => ({ ...prev, [id]: false })); // Keluar dari mode edit
            })
            .catch((e) => {
                console.error("Failed to update price", e);
                toast.error("Gagal memperbarui harga");
            });
    }

    const handleSearch = () => {
        fetchData(currentPage, search, selectedSubbrand);
    };

    const handleEditClick = (id, currentPrice) => {
        setIsEditing((prev) => ({ ...prev, [id]: true })); // Masuk ke mode edit
        setNewPrice((prev) => ({ ...prev, [id]: currentPrice })); // Set nilai awal input harga baru
    };

    const handleSaveClick = (id, oldPrice) => {
        if (newPrice[id] && newPrice[id] !== oldPrice) {
            savePrice(id, parseInt(oldPrice), parseInt(newPrice[id]));
        } else {
            toast.warning("Harga baru tidak valid atau sama dengan harga lama");
        }
    };

    return (
        <div className="min-h-screen bg-black text-white p-6">
            <h1 className="text-3xl font-bold mb-6 text-red-600">Daftar Harga Produk</h1>
            <div className="flex space-x-4 mb-6">
                <Input
                    type="text"
                    placeholder="Cari produk..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="bg-zinc-900 border-zinc-800 text-white placeholder-zinc-500 focus-visible:ring-red-600"
                />
                <Button
                    onClick={handleSearch}
                    variant="secondary"
                    className="font-medium px-6"
                >
                    Cari
                </Button>
            </div>
            <div className="py-4 flex space-x-2 overflow-x-auto whitespace-nowrap mb-6 scrollbar-thin scrollbar-thumb-zinc-800">
                {subbrand.data.map((item) => (
                    <Badge
                        key={item.id}
                        variant={selectedSubbrand === item.id ? "default" : "outline"}
                        className={`cursor-pointer px-4 py-2 text-sm font-semibold rounded-full select-none transition-colors ${
                            selectedSubbrand === item.id
                                ? "bg-red-600 hover:bg-red-700 text-white border-transparent"
                                : "bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border-zinc-800"
                        }`}
                        onClick={() => {
                            setSelectedSubbrand(selectedSubbrand === item.id ? "" : item.id);
                        }}
                    >
                        {item.label}
                    </Badge>
                ))}
            </div>
            <div className="space-y-4">
                {products.map((product) => (
                    <Card
                        key={product.id}
                        className="bg-zinc-950 border-zinc-800 text-white"
                    >
                        <CardContent className="flex justify-between items-center py-4 px-6">
                            <div className="text-lg font-semibold">{product.label}</div>
                            <div className="flex items-center space-x-4">
                                <span className="text-zinc-400">
                                    {formatCurrency(product.old_price)}
                                </span>
                                {isEditing[product.id] ? (
                                    <>
                                        <Input
                                            type="number"
                                            value={newPrice[product.id] || ""}
                                            onChange={(e) =>
                                                setNewPrice((prev) => ({
                                                    ...prev,
                                                    [product.id]: e.target.value,
                                                }))
                                            }
                                            className="w-32 bg-zinc-900 border-zinc-800 text-white focus-visible:ring-red-600"
                                        />
                                        <Button
                                            onClick={() =>
                                                handleSaveClick(product.id, product.active_price)
                                            }
                                            variant="secondary"
                                        >
                                            Submit
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        onClick={() =>
                                            handleEditClick(product.id, product.active_price)
                                        }
                                        variant="secondary"
                                    >
                                        Edit
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
            <div className="flex justify-center mt-6 flex-wrap gap-2">
                {Array.from({ length: totalPages }, (_, i) => (
                    <Button
                        key={i + 1}
                        onClick={() => setCurrentPage(i + 1)}
                        variant={currentPage === i + 1 ? "secondary" : "outline"}
                    >
                        {i + 1}
                    </Button>
                ))}
            </div>
        </div>
    );
}